'use client';

import { useEffect, useState } from 'react';
import { CamundaHistoryService } from '@/lib/camunda/history-service';
import { CamundaUserService } from '@/lib/camunda/user-service';
import { getHistoricTaskStatePresentation } from '@/lib/camunda/history-task-state';
import { getAvailableForms } from '@/lib/form-utilities';
import { resolveFormKey } from '@/lib/process-registry';
import { BoundedTtlCache } from '@/lib/bounded-ttl-cache';
import type { ProcessHistoryData, ProcessHistoryTaskItem } from './process-history-types';
import {
  applyHistoricTaskDetailAccess,
  applyTaskDisplayNames,
  buildSyntheticStartFormTask,
  findUnmatchedActiveTaskIds,
  hasStartFormInTaskList,
  resolveWorkableTaskId,
} from './process-history-utils';

type ProcessHistoryTaskDraft = Omit<
  ProcessHistoryTaskItem,
  'canViewDetail' | 'displayName' | 'taskOrdinal'
>;

const PROCESS_HISTORY_CACHE_TTL_MS = 30 * 1000;

const processHistoryCache = new BoundedTtlCache<string, ProcessHistoryData>(
  50,
  PROCESS_HISTORY_CACHE_TTL_MS,
);

interface UseProcessHistoryResult {
  data: ProcessHistoryData | null;
  loading: boolean;
  error: string | null;
  forbidden: boolean;
}

export function useProcessHistory(processInstanceId: string): UseProcessHistoryResult {
  const [data, setData] = useState<ProcessHistoryData | null>(null);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState<string | null>(null);
  const [forbidden, setForbidden] = useState(false);

  useEffect(() => {
    let cancelled = false;

    const load = async () => {
      setLoading(true);
      setError(null);
      setForbidden(false);
      setData(null);

      const performLoad = async () => {
        const currentUserId = CamundaUserService.getCurrentUserId();
        if (!currentUserId) {
          if (!cancelled) {
            setForbidden(true);
          }
          return;
        }

        const cacheKey = `${currentUserId}:${processInstanceId}`;
        const cached = processHistoryCache.get(cacheKey);
        if (cached) {
          if (!cancelled) {
            setData(cached);
          }
          return;
        }

        const backendDetail = await CamundaHistoryService.fetchParticipationProcessDetail(
          processInstanceId,
        );

        if (cancelled) return;

        if (backendDetail === 'forbidden') {
          setForbidden(true);
          return;
        }

        if (backendDetail === 'not_found') {
          setError('No se encontró el caso solicitado.');
          return;
        }

        if (backendDetail === 'error') {
          setError('No se pudo cargar el historial del caso.');
          return;
        }

        const {
          process,
          tasks: historicTasks,
          activeTasks: activeTasksFromBff,
          startForm,
          viewableTaskIds: viewableTaskIdList,
          workableTaskIds: workableTaskIdList,
          userDisplayNames,
          isReviewer,
          metadata,
          accessReasons,
        } = backendDetail;
        const viewableTaskIds = new Set(viewableTaskIdList);
        const workableTaskIds = new Set(workableTaskIdList);

        const rawTitulo = metadata.processInstanceTitle ?? metadata.titulo;
        const titulo =
          rawTitulo != null && String(rawTitulo).trim() !== '' ? String(rawTitulo) : null;
        const availableForms = getAvailableForms();

        const unmatchedActiveTaskIds = findUnmatchedActiveTaskIds(
          historicTasks,
          activeTasksFromBff,
        );
        if (unmatchedActiveTaskIds.length > 0) {
          console.warn(
            'Participation BFF devolvió tareas runtime sin contraparte histórica; se omiten del timeline.',
            {
              processInstanceId,
              activeTaskIds: unmatchedActiveTaskIds,
            },
          );
        }

        const tasksWithFormKey = historicTasks.map((task) => {
          const assigneeName = task.assignee
            ? userDisplayNames[task.assignee] ?? task.assignee
            : '-';
          const { label, className } = getHistoricTaskStatePresentation(task);

          return {
            task,
            formKey: task.formKey
              ? resolveFormKey(task.formKey, availableForms)
              : null,
            assigneeName,
            stateLabel: label,
            stateClassName: className,
          } satisfies ProcessHistoryTaskDraft;
        });

        if (cancelled) return;

        const tasks: ProcessHistoryTaskDraft[] = tasksWithFormKey;

        if (startForm && !hasStartFormInTaskList(tasks, startForm.activityId)) {
          const syntheticTask = buildSyntheticStartFormTask(process, startForm);
          const startFormKey = resolveFormKey(startForm.formKey, availableForms);
          const assigneeName = process.startUserId
            ? userDisplayNames[process.startUserId] ?? process.startUserId
            : '-';
          const { label, className } = getHistoricTaskStatePresentation(syntheticTask);

          tasks.push({
            task: syntheticTask,
            formKey: startFormKey,
            assigneeName,
            stateLabel: label,
            stateClassName: className,
            isStartForm: true,
          });
        }

        tasks.sort(
          (a, b) =>
            new Date(a.task.startTime).getTime() - new Date(b.task.startTime).getTime(),
        );

        const tasksWithWorkable = tasks.map((item) => ({
          ...item,
          workableTaskId: item.isStartForm
            ? null
            : resolveWorkableTaskId(item.task, workableTaskIds),
        }));

        const enrichedTasks = applyTaskDisplayNames(
          applyHistoricTaskDetailAccess(tasksWithWorkable, viewableTaskIds),
        );

        const loadedData = {
          process,
          titulo,
          accessReasons,
          tasks: enrichedTasks,
          isAdmin: isReviewer,
        };
        processHistoryCache.set(cacheKey, loadedData);
        setData(loadedData);
      };

      try {
        await performLoad();
      } catch (loadError) {
        console.error('Error loading process history:', loadError);
        if (!cancelled) {
          setError(
            loadError instanceof Error
              ? loadError.message
              : 'Error al cargar el historial del caso',
          );
        }
      }

      if (!cancelled) {
        setLoading(false);
      }
    };

    void load();

    return () => {
      cancelled = true;
    };
  }, [processInstanceId]);

  return { data, loading, error, forbidden };
}

export function findAdjacentProcessHistoryTasks(
  tasks: ProcessHistoryTaskItem[],
  currentTaskId: string,
) {
  const index = tasks.findIndex((item) => item.task.id === currentTaskId);
  if (index === -1) {
    return { previous: null, next: null };
  }

  return {
    previous: index > 0 ? tasks[index - 1] : null,
    next: index < tasks.length - 1 ? tasks[index + 1] : null,
  };
}
